import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { lineWrap, lineInner } from "@/lib/motion";

/* Section overline label with red accent bar */
export function SectionLabel({ children, light = false, className = "", testId }) {
  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid={testId}>
      <span className="h-[3px] w-8 bg-[#C8102E]" />
      <span
        className={`text-xs font-bold uppercase tracking-[0.22em] font-manrope ${
          light ? "text-white/70" : "text-[#C8102E]"
        }`}
      >
        {children}
      </span>
    </div>
  );
}

/* Big transparent stroked number */
export function GhostNumber({ children, light = false, className = "" }) {
  return (
    <span
      className={`ghost-number ${light ? "ghost-number-light" : ""} ${className}`}
      aria-hidden="true"
    >
      {children}
    </span>
  );
}

/* Kinetic hero heading — line-by-line masked reveal */
export function HeroHeading({ lines, className = "", testId }) {
  return (
    <motion.h1
      variants={lineWrap}
      initial="hidden"
      animate="show"
      className={className}
      data-testid={testId}
    >
      {lines.map((line, i) => (
        <span key={i} className="line-mask">
          <motion.span variants={lineInner} className="block">
            {line}
          </motion.span>
        </span>
      ))}
    </motion.h1>
  );
}

/* Primary / Secondary CTA buttons (router links or anchors) */
const primaryCls =
  "group inline-flex items-center justify-center gap-2 bg-[#C8102E] text-white font-manrope font-semibold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-[#A00B24]";
const secondaryCls =
  "group inline-flex items-center justify-center gap-2 border-2 border-[#0A1F44] text-[#0A1F44] font-manrope font-semibold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-[#0A1F44] hover:text-white";
const secondaryLightCls =
  "group inline-flex items-center justify-center gap-2 border-2 border-white/70 text-white font-manrope font-semibold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-white hover:text-[#0A1F44]";

export function CTA({ to, href, children, variant = "primary", testId, onClick }) {
  const cls =
    variant === "secondary"
      ? secondaryCls
      : variant === "secondary-light"
      ? secondaryLightCls
      : primaryCls;
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} data-testid={testId}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls} data-testid={testId}>
      {children}
    </button>
  );
}

/* Inner-page hero */
export function PageHero({ label, lines, sub, children, testId }) {
  return (
    <section className="relative bg-white pt-36 lg:pt-44 pb-16 lg:pb-20 overflow-hidden" data-testid={testId}>
      <div className="absolute top-0 right-0 w-[50%] h-full flag-stripes opacity-60 pointer-events-none" />
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#0A1F44]/5 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <SectionLabel>{label}</SectionLabel>
        </motion.div>
        <HeroHeading
          className="mt-6 font-outfit font-extrabold text-[#0A1F44] text-4xl sm:text-5xl lg:text-[4rem] leading-[0.98] tracking-tight max-w-5xl"
          lines={lines}
        />
        {sub && (
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mt-7 text-[#12294D]/75 text-lg leading-relaxed max-w-3xl font-manrope"
          >
            {sub}
          </motion.p>
        )}
        {children && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.7 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            {children}
          </motion.div>
        )}
      </div>
    </section>
  );
}

/* Slow editorial marquee ribbon */
export function EditorialMarquee() {
  const items = [
    "PRE-VERIFIED CANDIDATES",
    "RIGHT TO WORK",
    "BRP VERIFICATION",
    "CAS VALIDATED",
    "FULL COMPLIANCE",
    "SOUTHAMPTON",
    "LONDON",
    "ALL ROLE TYPES",
  ];
  return (
    <div className="bg-[#0A1F44] py-6 overflow-hidden relative grain" data-testid="editorial-marquee">
      <Marquee speed={40} gradient={false} autoFill>
        {items.map((t, i) => (
          <span key={i} className="flex items-center">
            <span className="marquee-outline text-4xl md:text-6xl px-8 uppercase tracking-tight">
              {t}
            </span>
            <span className="h-2 w-2 rounded-full bg-[#C8102E]" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}
